import React, { Fragment, useEffect, useState, useContext } from "react";
import { Container, Row, Col, Form, Alert } from "react-bootstrap";
import axios from "axios";
import { AuthContext } from "../../../Context/AuthContext";
import { Authorization } from "../../../Context/Authorization";
import Preloader from "../../../shared/PreLoaders";
import { logActivity } from "../../../utils/LogActivity";
import { toast } from "react-toastify";

const IndividualLoanRepayment = () => {
  const [vfcNames, setVfcNames] = useState([]);
  const [loanNumbers, setLoanNumbers] = useState([]);
  const [selectedVfc, setSelectedVfc] = useState("");
  const [selectedLoan, setSelectedLoan] = useState("");
  const [tableData, setTableData] = useState([]);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showTable, setShowTable] = useState(false);
  const { userId } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const Api = process.env.REACT_APP_API;

  useEffect(() => {
    setLoading(true);
    const fetchVfc = async () => {
      try {
        const config = Authorization();
        const response = await axios.get(`${Api}/api/vfcs `, config);
        const data = response.data.vfcs;
        setVfcNames(data);
      } catch (error) {
        console.log("Error fetching Data", error);
      }
      setLoading(false);
    };

    fetchVfc();
  }, [Api]);

  const handleVfcChange = async (event) => {
    const vfcId = event.target.value;
    setSelectedVfc(vfcId);
    setLoanNumbers([]);
    setSelectedLoan("");
    setTableData([]);
    setShowTable(false);
    setError(null);
    setSuccess(null);
    setLoading(true);

    if (vfcId) {
      try {
        const config = Authorization();
        const response = await fetch(`${Api}/api/loans/getLoanByVfcId`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...config.headers,
          },
          body: JSON.stringify({ vfcId }),
        });
        if (!response.ok)
          throw new Error(`HTTP error! Status: ${response.status}`);

        const data = await response.json();
        const loans = data.loans || [];
        if (Array.isArray(loans)) {
          const groupLoans = loans.filter(
            (loan) => loan.groupType === "Individual"
          );

          setLoanNumbers(groupLoans);
        } else {
          throw new Error("Invalid data format: Expected an array of loans.");
        }
      } catch (error) {
        console.error("Error:", error);
        setError(`Failed to fetch loan numbers. ${error.message}`);
      }
    }
    setLoading(false);
  };

  const handleLoanChange = async (event) => {
    const loanId = event.target.value;
    setSelectedLoan(loanId);
    setTableData([]);
    setShowTable(false);
    setError(null);
    setSuccess(null);
    setLoading(true);

    if (loanId) {
      try {
        const selectedLoanData = loanNumbers.find(
          (loan) => loan._id === loanId
        );

        if (!selectedLoanData) {
          throw new Error("Loan data not found");
        }
        const config = Authorization();
        const response = await fetch(`${Api}/api/loans/getLoanByLoanNo`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...config.headers,
          },
          body: JSON.stringify({ loanNo: selectedLoanData.loanNo }), // Send loanNo instead of loanId
        });

        if (!response.ok) throw new Error("Error fetching loan collections");

        const data = await response.json();

        if (data.groupType === "Individual") {
          const flattenedData = data.subLoans.map((subLoan) => ({
            ...subLoan,
            loanNumber: data.loanNo,
            loanAmount: subLoan.disbursedAmount,
            name: subLoan.memberId ? subLoan.memberId.name : "Unknown",
            totalLoanPaid: data.totalLoanPaid || 0, // needed for handleInputChange
            balance: subLoan.disbursedAmount - (data.totalLoanPaid || 0),
            principal: 0,
            interest: 0,
            total: 0,
            shgId: data.shgId,
            memberId: subLoan.memberId ? subLoan.memberId._id : null,
          }));

          setTableData(flattenedData);
        } else {
          setError("The selected loan is not an individual loan.");
        }
      } catch (error) {
        console.log(error.message);
        setError("Failed to fetch loan collections. Please try again.");
      }
    }
    setLoading(false);
  };

  const handleInputChange = (index, field, value) => {
    const newTableData = [...tableData];
    const parsedValue =
      field === "paymentMode" ? value : parseFloat(value) || 0;
    newTableData[index][field] = parsedValue;

    if (field === "principal" || field === "interest") {
      const principal = parseFloat(newTableData[index].principal) || 0;
      const interest = parseFloat(newTableData[index].interest) || 0;

      const total = principal + interest;
      newTableData[index].total = total;

      const originalLoanAmount = newTableData[index].loanAmount || 0;
      const alreadyPaid = newTableData[index].totalLoanPaid || 0;

      newTableData[index].balance =
        originalLoanAmount - alreadyPaid - principal;
    }

    setTableData(newTableData);
  };

  const handleSubmit = async () => {
    try {
      const payload = tableData.map((loan) => ({
        loanId: selectedLoan,
        subLoanId: loan._id,
        shgId: loan.shgId,
        memberId: loan.memberId,
        amountPaid: loan.principal,
        interestPaid: loan.interest,
        cashOrBank: loan.paymentMode, // Include payment mode
      }));
      const config = Authorization();
      const response = await fetch(`${Api}/api/loan-collections`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...config.headers,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) throw new Error("Error saving loan collections");

      setSuccess("Loan collections saved successfully");
      toast.success("Added Successfully");
      await logActivity(
        userId,
        "New Individual Loan Repayment",
        "Individual Loan Repayment Page",
        Api
      );

      // Fetch updated loan details
      await handleLoanChange({ target: { value: selectedLoan } });
      setShowTable(false);
      setTableData([]);
      setSelectedLoan("");
      setError(null);
    } catch (error) {
      console.log(error.message);
      setError("Failed to save loan collections. Please try again.");
      setSuccess(null);
    }
  };

  const handleShowTable = () => {
    if (selectedLoan) {
      setShowTable(true);
    } else {
      setError("Please select a loan number first.");
    }
  };

  return (
    <Fragment>
      <Preloader loading={loading} />

      <div className="page-header">
        <h3 className="page-title"> Individual Loan Repayments </h3>
        <nav aria-label="breadcrumb">
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <a href="!#" onClick={(event) => event.preventDefault()}>
                VFC
              </a>
            </li>
            <li className="breadcrumb-item active" aria-current="page">
              Individual Loan Repayments
            </li>
          </ol>
        </nav>
      </div>

      <Container style={{ marginTop: "20px", padding: "20px" }}>
        {error && (
          <Row>
            <Col>
              <Alert variant="danger">{error}</Alert>
            </Col>
          </Row>
        )}
        {success && (
          <Row>
            <Col>
              <Alert variant="success">{success}</Alert>
            </Col>
          </Row>
        )}
        <Row className="mb-3">
          <div className="col-md-12 grid-margin stretch-card">
            <div className="card">
              <div className="card-body">
                <form>
                  <Form.Group controlId="formVFC">
                    <Form.Label>VFC</Form.Label>
                    <div className="input-container">
                      <Form.Control
                        as="select"
                        onChange={handleVfcChange}
                        value={selectedVfc}
                      >
                        <option value="">Select VFC</option>
                        {vfcNames.map((vfc) => (
                          <option key={vfc._id} value={vfc._id}>
                            {vfc.vfcName}
                          </option>
                        ))}
                      </Form.Control>
                    </div>
                  </Form.Group>
                  <Form.Group controlId="formLoanNo">
                    <Form.Label>Loan No</Form.Label>
                    <div className="input-container">
                      <Form.Control
                        as="select"
                        onChange={handleLoanChange}
                        value={selectedLoan}
                        disabled={!selectedVfc}
                      >
                        <option value="">Select Loan No</option>
                        {loanNumbers.map((loan) =>
                          loan.subLoans.map((subLoan) => (
                            <option key={subLoan.subLoanId} value={loan._id}>
                              {loan.loanNo} - {subLoan.memberId.name}
                            </option>
                          ))
                        )}
                      </Form.Control>
                    </div>
                  </Form.Group>
                </form>
              </div>
            </div>
          </div>
        </Row>

        <Row className="mb-3">
          <Col>
            <button
              type="button"
              className="btn btn-outline-primary btn-icon-text"
              onClick={handleShowTable}
            >
              <i className="mdi mdi-file-check btn-icon-prepend"></i>
              Show Loan Details
            </button>
          </Col>
        </Row>
        <Row>
          <Col>
            {showTable && (
              <div className="card">
                <div className="card-body">
                  <div className="row icons-list">
                    <div className="Table_data">
                      <table className="table">
                        <thead>
                          <tr>
                            <th>Loan No</th>
                            <th>Loan Amount</th>
                            <th>Name</th>
                            <th>Principal</th>
                            <th>Interest</th>
                            <th>Total</th>
                            <th>Balance</th>
                            <th>Payment Mode</th>{" "}
                            {/* New Column for Cash/Bank */}
                          </tr>
                        </thead>
                        <tbody>
                          {tableData.map((loan, index) => (
                            <tr key={loan._id}>
                              <td>{loan.loanNumber}</td>
                              <td>{loan.loanAmount}</td>
                              <td>{loan.name}</td>
                              <td>
                                <Form.Control
                                  type="number"
                                  value={loan.principal}
                                  onChange={(e) =>
                                    handleInputChange(
                                      index,
                                      "principal",
                                      e.target.value
                                    )
                                  }
                                />
                              </td>
                              <td>
                                <Form.Control
                                  type="number"
                                  value={loan.interest}
                                  onChange={(e) =>
                                    handleInputChange(
                                      index,
                                      "interest",
                                      e.target.value
                                    )
                                  }
                                />
                              </td>
                              <td>{loan.total}</td>
                              <td>{loan.balance}</td>
                              <td>
                                <Form.Control
                                  as="select"
                                  value={
                                    loan.loanAmount - (loan.totalLoanPaid || 0)
                                  }
                                  onChange={(e) =>
                                    handleInputChange(
                                      index,
                                      "paymentMode",
                                      e.target.value
                                    )
                                  }
                                >
                                  <option value="">Select</option>
                                  <option value="cash">Cash</option>
                                  <option value="bank">Bank</option>
                                </Form.Control>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </Col>
        </Row>
        {showTable && (
          <Row className="mt-3">
            <Col>
              <button
                type="button"
                className="btn btn-outline-primary btn-icon-text mt-3"
                onClick={handleSubmit}
              >
                <i className="mdi mdi-file-check btn-icon-prepend"></i>
                Submit
              </button>
            </Col>
          </Row>
        )}
      </Container>
    </Fragment>
  );
};

export default IndividualLoanRepayment;
